import React, { useEffect, useMemo, useState } from 'react';

/* External */
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import MaintainersPaginator from 'commons/components/MaintainersPaginator';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
  makeStyles,
  Typography,
  Avatar,
  Fab,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Grid,
  Container,
  CardContent,
  CardHeader,
  TextField,
  Card,
  Box,
  Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

/* Project */
import DeleteModal from 'commons/components/DeleteModal';
import { PROCESS_CATEGORIES } from 'screens/ProcessCategories/reducer';
import { MODULES_ACTIONS } from './Modules.reducer';
import styles from './modules.styles';
import ModuleForm from './components/ModuleForm';

const useStyles = makeStyles(styles);

function ModulesScreen() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const classes = useStyles();

  const [deleteError, setDeleteError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    moduleName: '',
    categoryId: 0,
  });
  const [selectedModule, setSelectedModule] = useState(null);

  const rawObjects = useSelector((state) => state.app.objects);
  const modules = useSelector((state) => state.modules.modules);
  const deleteLoading = useSelector((state) => state.modules.deleteLoading);
  const moduleToDelete = useSelector((state) => state.modules.moduleToDelete);
  const creationFormOpen = useSelector((state) => state.modules.creationFormOpen);

  const categories = useMemo(
    () => (rawObjects ? Object.values(rawObjects.processCategories) : []),
    [rawObjects.processCategories],
  );

  useEffect(() => {
    dispatch({
      type: PROCESS_CATEGORIES.GET_STATE_FROM_API,
    });

    dispatch({
      type: MODULES_ACTIONS.GET_STATE_FROM_API,
    });
  }, []);

  const sortedModules = useMemo(
    () => [...modules].sort((a, b) => a.name.localeCompare(b.name)),
    [modules],
  );

  const filteredModules = sortedModules.filter(
    (module) =>
      module.name.toLowerCase().includes(filters.moduleName.toLowerCase()) &&
      (filters.categoryId ? module.categoryId === filters.categoryId : true),
  );

  const setSelectedItem = (module) => {
    setSelectedModule(module);
  };

  const openForm = () => {
    dispatch({
      type: MODULES_ACTIONS.OPEN_CREATION_FORM,
      payload: { moduleSelected: null },
    });
  };

  const closeForm = () => {
    dispatch({
      type: MODULES_ACTIONS.CLOSE_CREATION_FORM,
    });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleItemDeletion = () => {
    if (!moduleToDelete) return;

    dispatch({
      type: MODULES_ACTIONS.DELETE_ITEM,
      payload: {
        id: moduleToDelete.id,
        onSuccess: () => {
          setSelectedItem(null);
          setDeleteError('');
        },
        onError: () => {
          setDeleteError('Error in the deletion of the module. Please try again later.');
        },
      },
    });
  };

  const onFiltersChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditModule = () => {
    if (!selectedModule) return;

    dispatch({
      type: MODULES_ACTIONS.OPEN_CREATION_FORM,
      payload: { module: selectedModule },
    });

    setAnchorEl(null);
  };

  const handleDeleteModule = () => {
    if (!selectedModule) return;

    dispatch({
      type: MODULES_ACTIONS.SET_MODULE_TO_DELETE,
      payload: { module: selectedModule },
    });

    setAnchorEl(null);
  };

  const handleMenuClick = (event, module) => {
    setAnchorEl(event.currentTarget);
    setSelectedModule(module);
  };

  const closeDeleteModal = () => {
    dispatch({
      type: MODULES_ACTIONS.SET_MODULE_TO_DELETE,
      payload: { module: null },
    });
  };

  const getListItem = (module) => (
    <ListItem key={`module-${module.id}`}>
      <ListItemAvatar>
        <Avatar className={classes.blue}>{(module.name || 'm')[0].toUpperCase()}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" width="100%">
            <Typography variant="body1">{module.name}</Typography>
            <Chip
              className={classes.chip}
              label={categories.find((cat) => cat.id === module.categoryId)?.name}
            />
          </Box>
        }
      />

      <ListItemSecondaryAction>
        <IconButton
          data-id={module.id}
          onClick={(e) => {
            handleMenuClick(e, module);
          }}
          aria-controls={`menu-module-${module.id}`}>
          <MoreVertIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h3" component="h2" className={classes.marginTop}>
            {t('modules.title')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.root}>
            <CardHeader title={t('Filter')} />
            <CardContent>
              <Grid container spacing={3} className={classes.container}>
                <Grid container item xs={4}>
                  <TextField
                    fullWidth
                    id="standard-basic"
                    label={t('modules.filters.name')}
                    value={filters.moduleName}
                    name="moduleName"
                    onChange={onFiltersChange}
                  />
                </Grid>
                <Grid container item xs={4}>
                  <Autocomplete
                    id="categories-selector"
                    options={categories}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    style={{ width: 300 }}
                    value={
                      categories.find((category) => category.id === filters.categoryId) || null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('modules.filters.category')}
                        variant="standard"
                      />
                    )}
                    onChange={(event, newValue) => {
                      setFilters({
                        ...filters,
                        categoryId: newValue?.id,
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card className={classes.root}>
            <CardContent>
              {filteredModules?.length ? (
                <MaintainersPaginator
                  classes={classes}
                  list={filteredModules}
                  getListItem={getListItem}
                />
              ) : (
                <Grid container item xs={12} alignItems="center" justifyContent="center">
                  <Typography variant="h6" component="h2">
                    {t('modules.noModulesFound')}
                  </Typography>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Menu anchorEl={anchorEl} keepMounted open={!!anchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={handleEditModule}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          {t('modules.actions.edit')}
        </MenuItem>
        <MenuItem onClick={handleDeleteModule}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          {t('modules.actions.delete')}
        </MenuItem>
      </Menu>

      <ModuleForm
        isOpen={creationFormOpen}
        closeForm={closeForm}
        categories={categories}
        classes={classes}
      />

      <DeleteModal
        isOpen={!!moduleToDelete}
        toggleForm={closeDeleteModal}
        submit={handleItemDeletion}
        buttonId="deleteModule"
        loading={deleteLoading}
        error={deleteError}
      />

      <Fab color="primary" aria-label="add" className={classes.fab} onClick={openForm}>
        <AddIcon />
      </Fab>
    </Container>
  );
}

export default ModulesScreen;
